<template>
  <section :key="'movie_player'" class="player-contents">
    <div class="video-wrap">
      <!-- 플레이어 콘텐츠 영역 -->
      <div class="video-wrap">
        <div class="player-contents">
          <div class="player-area">
            <TrailerPlayer
              v-if="hasTrailer"
              :content-info="contentInfo"
              current-section="movie"
              class="active"
            />
            <PosterHorizontal
              v-if="contentImage.isHorizontal"
              :poster-image="contentImage.image"
              :poster-alt-text="movieTitle"
            />
            <PosterVertical
              v-else
              :poster-image="contentImage.image"
              :poster-alt-text="movieTitle"
            />
          </div>
          <BtnToggleMuteTrailer
            v-if="isShowTrailerMuteBtn"
            :content-info="contentInfo"
          />

          <div class="video-detail-area">
            <h1 class="logo-box">
              <BrandBadge v-if="brandLogo" :brand-logo="brandLogo" />
              <em v-if="movieLogoImage" class="logo"
                ><img
                  :src="getMovieLogoImage(movieLogoImage)"
                  width=""
                  height=""
                  :alt="movieTitle"
              /></em>
              <em v-else class="title">{{ movieTitle }}</em>
            </h1>

            <PlayerMetaData
              :key="`${movieTitle}__meta_data`"
              :content-info="contentInfo"
              :content-type="contentType"
            />
            <PlayerComments
              :key="`${movieTitle}__player_comments`"
              :comment-list="comments"
            />

            <div class="essential-box">
              <div v-if="hasMovieGroup" class="select-box">
                <PlayerFilterList
                  :key="`${movieTitle}__movie_group_list`"
                  :current-id="movieId"
                  :filter-item="movieGroup"
                />
              </div>
              <PlayerActionButton
                :key="`${movieTitle}__player_action_button`"
                :content-info="contentInfo"
                :content-type="contentType"
              />
              <PlayerIconButtons
                v-if="!isWavveOn"
                :key="`${movieTitle}__icon_button`"
                :content-info="contentInfo"
                :content-type="contentType"
                @on-click-share="openSharePopup"
              />
            </div>

            <PlayerDetail
              :key="`${movieTitle}__detail_info`"
              :content-info="contentInfo"
              :content-type="contentType"
              @on-click-more="moveToDetailInfo"
            />

            <PlayerNotice
              :key="`${movieTitle}__notice`"
              :content-id="movieId"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import _ from 'lodash'
  import cookie from '@/common/utils/cookie'
  import {
    adjustHeight,
    movieInfoBox,
    topTagList,
  } from '@/common/resources/directives'
  import { MOVE_TO_DETAIL_TAB } from '@/common/resources/const'
  import {
    PLAYER_TYPE,
    TRAILER_ATTR_BY_PLAYER_TYPE,
  } from '@/common/resources/player.const'
  import { titleTagNameChange } from '@/common/utils/common'
  import { resizeThumbnail } from '@/common/utils/image'

  // mixins
  import PlayerDetailInfoMixin from '@/mixins/player.detail.info.mixin'
  import TrailerPlayerPageMixin from '@/mixins/trailer.player.page.mixin'

  // components
  import BrandBadge from '@/components/player/BrandBadge'
  import BtnToggleMuteTrailer from '@/components/player/BtnToggleMuteTrailer'
  import PlayerActionButton from '@/components/player/detail/PlayerActionButton'
  import PlayerComments from '@/components/player/detail/PlayerComments.vue'
  import PlayerDetail from '@/components/player/detail/PlayerDetail.vue'
  import PlayerFilterList from '@/components/player/PlayerFilterList'
  import PlayerIconButtons from '@/components/player/detail/PlayerIconButtons.vue'
  import PlayerMetaData from '@/components/player/detail/PlayerMetaData.vue'
  import PlayerNotice from '@/components/player/PlayerNotice'
  import PosterHorizontal from '@/components/player/PosterHorizontal'
  import PosterVertical from '@/components/player/PosterVertical'
  import TrailerPlayer from '@/components/player/TrailerPlayer'

  export default {
    name: 'MoviePlayer',
    components: {
      PlayerComments,
      PlayerDetail,
      PlayerIconButtons,
      PlayerFilterList,
      PlayerActionButton,
      PlayerNotice,
      PlayerMetaData,
      BrandBadge,
      PosterVertical,
      PosterHorizontal,
      TrailerPlayer,
      BtnToggleMuteTrailer,
    },
    directives: {
      movieInfoBox,
      topTagList,
      adjustHeight,
    },
    mixins: [PlayerDetailInfoMixin, TrailerPlayerPageMixin],
    props: {
      contentInfo: {
        type: Object,
        default: () => ({}),
      },
      openSharePopup: {
        type: Function,
      },
    },
    data() {
      return {
        isWavveOn: cookie.isWavveOn(),
        movieId: _.get(this.$route, 'query.movieid', ''),
        contentType: PLAYER_TYPE.MOVIE,
      }
    },
    computed: {
      brandLogo() {
        return _.get(this.contentInfo, 'brand_logolist.0', '')
      },
      contentImage() {
        const {
          moviegroup_horizontal_logoN_image: imageHorizonWithoutLogo = '',
          moviegroup_vertical_logoY_image: imageVerticalWithLogo = '',
        } = this.contentInfo

        const isHorizontal = imageHorizonWithoutLogo.length > 0
        const image = isHorizontal
          ? imageHorizonWithoutLogo
          : imageVerticalWithLogo
        return { isHorizontal, image }
      },
      comment() {
        return _.get(this.contentInfo, 'comment', '')
      },
      comments() {
        const { autocomment = '' } = this.contentInfo
        return [autocomment, this.comment]
      },
      movieGroup() {
        return _.get(this.contentInfo, 'moviegroup', {})
      },
      movieTitle() {
        return _.get(this.contentInfo, 'title', '')
      },
      movieLogoImage() {
        return _.get(this.contentInfo, 'moviegrouptitlelogoimage', '')
      },
      hasMovieGroup() {
        return Number(_.get(this.contentInfo, 'moviegroup.count', '0')) > 1
      },
      hasTrailer() {
        return (
          _.get(
            this.contentInfo,
            TRAILER_ATTR_BY_PLAYER_TYPE['movie'].id,
            '',
          ) !== ''
        )
      },
      /**
       * 예고편 플레이어 음소거 버튼 노출 여부 (트레일러가 있고 재생상태라면)
       * @returns {false|boolean}
       */
      isShowTrailerMuteBtn() {
        return this.hasTrailer && this.isTrailerPlaying
      },
    },
    watch: {
      async contentInfo(newContentInfo) {
        this.setPageTitle()
        const newMovieId = _.get(newContentInfo, 'movieid', '')
        this.movieId = newMovieId
      },
    },
    created() {
      this.setPageTitle()
    },
    methods: {
      get: _.get,
      setPageTitle() {
        titleTagNameChange(`${this.movieTitle || '영화 상세화면'} : 웨이브`)
      },
      getMovieLogoImage(img) {
        return resizeThumbnail(img, {
          width: 0,
          height: 0,
          option: 20,
          quality: 80,
        })
      },
      moveToDetailInfo() {
        this.$events.$emit(MOVE_TO_DETAIL_TAB)
      },
    },
  }
</script>
